// Backend-wired Notifications panel for the Me/Settings screen.
// Persists toggles to notification_preferences and surfaces the push channel state.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getNotificationPrefs,
  setNotificationPref,
  pushUnsubscribe,
} from "@/lib/notifications.functions";
import { PushOptInPrompt } from "./PushOptInPrompt";

const T = "#FF5C7A";
const TL = "#FFF0F2";
const GD = "#2A1228";
const GR = "#6E4B71";
const GL = "#F2EAEC";
const WH = "#ffffff";
const PL = "#4B1F4F";

type UiKey = "dailyCheckin" | "hrtReminder" | "weeklyReview" | "milestones";

const TYPE_MAP: Record<UiKey, "daily_checkin" | "hrt_reminder" | "weekly_review" | "milestone"> = {
  dailyCheckin: "daily_checkin",
  hrtReminder: "hrt_reminder",
  weeklyReview: "weekly_review",
  milestones: "milestone",
};

interface Item {
  key: UiKey;
  emoji: string;
  label: string;
  desc: string;
  time: string;
}

export function NotificationsPanel({
  onHRT,
  hrtType,
}: {
  onHRT: boolean;
  hrtType?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [notifs, setNotifs] = useState<Record<UiKey, boolean>>({
    dailyCheckin: true,
    hrtReminder: true,
    weeklyReview: true,
    milestones: true,
  });
  const [hasPush, setHasPush] = useState(false);
  const [showOptIn, setShowOptIn] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const getPrefs = useServerFn(getNotificationPrefs);
  const setPref = useServerFn(setNotificationPref);
  const unsubscribeFn = useServerFn(pushUnsubscribe);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as any).standalone === true;
    setIsStandalone(standalone);

    getPrefs()
      .then((res) => {
        if (!res) return;
        setNotifs({
          dailyCheckin: res.prefs.daily_checkin?.enabled ?? true,
          hrtReminder: res.prefs.hrt_reminder?.enabled ?? true,
          weeklyReview: res.prefs.weekly_review?.enabled ?? true,
          milestones: res.prefs.milestone?.enabled ?? true,
        });
        setHasPush(!!res.hasActivePush);
      })
      .catch((e) => console.warn("[notif-panel] load failed", e))
      .finally(() => setLoaded(true));
  }, []);

  const toggle = async (k: UiKey) => {
    const next = !notifs[k];
    setNotifs((n) => ({ ...n, [k]: next }));
    try {
      await setPref({ data: { type: TYPE_MAP[k], enabled: next } });
    } catch (e) {
      console.warn("[notif-panel] save failed", e);
      // revert on failure
      setNotifs((n) => ({ ...n, [k]: !next }));
    }
  };

  const switchToEmail = async () => {
    try {
      await unsubscribeFn({ data: {} });
      // best-effort: unregister from browser too
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        await sub?.unsubscribe();
      } catch {}
      setHasPush(false);
    } catch (e) {
      console.warn("[notif-panel] unsubscribe failed", e);
    }
  };

  const items: Item[] = [
    {
      key: "dailyCheckin",
      emoji: "📋",
      label: "Daily check-in reminder",
      desc: "Nudge to log your mood and symptoms each day",
      time: "8:00 PM daily",
    },
    ...(onHRT
      ? [
          {
            key: "hrtReminder" as UiKey,
            emoji: "💊",
            label: "HRT reminder",
            desc: `Remind you to take your ${hrtType || "medication"} at your usual time`,
            time: "Based on your schedule",
          },
        ]
      : []),
    {
      key: "weeklyReview",
      emoji: "📊",
      label: "Weekly review summary",
      desc: "Your week in review — every Sunday morning",
      time: "9:00 AM Sunday",
    },
    {
      key: "milestones",
      emoji: "🏆",
      label: "Milestone celebrations",
      desc: "Alert when you hit Day 7, 14, 30, or 90",
      time: "On milestone day",
    },
  ];

  return (
    <div
      style={{
        background: WH,
        borderRadius: 18,
        padding: 16,
        marginBottom: 0,
        border: "0.5px solid #F2EAEC",
      }}
    >
      {/* Channel banner */}
      {loaded && (
        <div
          style={{
            marginBottom: 14,
            padding: "12px 12px",
            borderRadius: 12,
            background: hasPush ? "#EAF6EE" : "#F5EEF6",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {hasPush ? (
            <>
              <div style={{ fontSize: 12.5, color: "#1E5A36", fontWeight: 500 }}>
                ✅ Push notifications enabled
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <button
                  onClick={() => setShowOptIn(true)}
                  style={{
                    fontSize: 11.5,
                    background: "transparent",
                    color: "#1E5A36",
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0,
                  }}
                >
                  Re-subscribe
                </button>
                <button
                  onClick={switchToEmail}
                  style={{
                    fontSize: 11.5,
                    background: "transparent",
                    color: "#1E5A36",
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0,
                  }}
                >
                  Switch to email only
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: PL }}>
                Reminders sent by email
              </div>
              <button
                onClick={() => setShowOptIn(true)}
                style={{
                  alignSelf: "flex-start",
                  fontSize: 12.5,
                  background: PL,
                  color: WH,
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {isStandalone ? "Enable push for instant reminders" : "Enable push notifications"}
              </button>
            </>
          )}

        </div>
      )}

      {items.map((n, i, a) => (
        <div
          key={n.key}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            paddingBottom: i < a.length - 1 ? 14 : 0,
            marginBottom: i < a.length - 1 ? 14 : 0,
            borderBottom: i < a.length - 1 ? "0.5px solid #E8D5DA" : "none",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: notifs[n.key] ? TL : GL,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {n.emoji}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 3,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: GD }}>
                {n.label}
              </div>
              <button
                onClick={() => toggle(n.key)}
                aria-label={`Toggle ${n.label}`}
                style={{
                  width: 44,
                  height: 26,
                  borderRadius: 13,
                  background: notifs[n.key] ? T : GL,
                  border: "none",
                  cursor: "pointer",
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: WH,
                    position: "absolute",
                    top: 3,
                    left: notifs[n.key] ? 21 : 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
              </button>
            </div>
            <div
              style={{
                fontSize: 11,
                color: GR,
                lineHeight: 1.5,
                marginBottom: 2,
              }}
            >
              {n.desc}
            </div>
            <div
              style={{
                fontSize: 10,
                color: notifs[n.key] ? T : GR,
                fontWeight: notifs[n.key] ? 500 : 400,
              }}
            >
              {notifs[n.key] ? n.time : "Off"}
            </div>
          </div>
        </div>
      ))}

      {showOptIn && (
        <PushOptInPrompt
          forceOpen
          onClose={() => {
            setShowOptIn(false);
            // Refetch to detect newly active push
            getPrefs()
              .then((r) => setHasPush(!!r?.hasActivePush))
              .catch(() => {});
          }}
        />
      )}
    </div>
  );
}

export default NotificationsPanel;
